
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import Head from 'next/head';
import { GetStaticProps } from 'next';
import { FaTwitter, FaFacebook, FaLinkedin, FaLink } from 'react-icons/fa';
import { HiSparkles, HiPhotograph } from 'react-icons/hi';
import { BiRefresh } from 'react-icons/bi';
import { AiOutlineClose, AiOutlineCheck } from 'react-icons/ai';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';
interface QuizImage {
    url: string;
    originalUrl?: string;
    isAI: boolean;
    source?: string;
    description?: string;
    slug?: string;
    photographer?: {
        name: string;
        username: string;
        profileUrl: string;
    };
    attribution?: string;
}
interface GameStats {
    correct: number;
    incorrect: number;
    streak: number;
    bestStreak: number;
}
const AIOrRealQuiz = () => {
    const [images, setImages] = useState<QuizImage[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [userChoice, setUserChoice] = useState<boolean | null>(null);
    const [showResult, setShowResult] = useState(false);
    const [gameStats, setGameStats] = useState<GameStats>({
        correct: 0,
        incorrect: 0,
        streak: 0,
        bestStreak: 0,
    });
    const [loading, setLoading] = useState(true);
    const [gameComplete, setGameComplete] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState<'ai' | 'real' | null>(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [autoHideTimer, setAutoHideTimer] = useState<NodeJS.Timeout | null>(null);
    const TOTAL_QUESTIONS = 10;
    useEffect(() => {
        fetchImages();
    }, []);
    // Preload images for smoother transitions
    useEffect(() => {
        if (images.length === 0)
            return;
        // Preload the next 3 images
        const preloadCount = 3;
        const preloadedImages: HTMLImageElement[] = [];
        for (let i = 1; i <= preloadCount; i++) {
            const nextIndex = currentIndex + i;
            if (nextIndex < images.length) {
                const img = new Image();
                img.src = images[nextIndex].url;
                // Optional: Set loading priority
                img.loading = 'eager';
                preloadedImages.push(img);
            }
        }
        // Also preload the previous image for smooth back navigation if implemented
        if (currentIndex > 0) {
            const prevImg = new Image();
            prevImg.src = images[currentIndex - 1].url;
            prevImg.loading = 'eager';
        }
        // Clean up function to potentially cancel loads if component unmounts
        return () => {
            preloadedImages.forEach((img) => {
                img.src = ''; // Cancel pending loads
            });
        };
    }, [currentIndex, images]);
    const fetchImages = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/quiz/ai-or-real');
            const data = await response.json();
            setImages(data.images);
            // Preload all images in the background for instant transitions
            if (data.images && data.images.length > 0) {
                // Start preloading all images immediately
                data.images.forEach((image: QuizImage, index: number) => {
                    const img = new Image();
                    img.src = image.url;
                    // Load first few images with high priority
                    if (index < 3) {
                        img.loading = 'eager';
                    }
                    else {
                        img.loading = 'lazy';
                    }
                });
            }
        }
        catch (error) {
            console.error('Failed to fetch images:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleChoice = (isAI: boolean) => {
        if (showResult)
            return;
        setSelectedAnswer(isAI ? 'ai' : 'real');
        setUserChoice(isAI);
        setShowResult(true);
        const correct = isAI === images[currentIndex].isAI;
        if (correct) {
            // Trigger confetti for correct answer
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 3000);
            setGameStats((prev) => ({
                ...prev,
                correct: prev.correct + 1,
                streak: prev.streak + 1,
                bestStreak: Math.max(prev.bestStreak, prev.streak + 1),
            }));
        }
        else {
            setGameStats((prev) => ({
                ...prev,
                incorrect: prev.incorrect + 1,
                streak: 0,
            }));
        }
        // Auto advance after 2 seconds if not hovering
        const timer = setTimeout(() => {
            if (!hovering) {
                handleNext();
            }
        }, 2000);
        setAutoHideTimer(timer);
    };
    const handleNext = () => {
        if (autoHideTimer) {
            clearTimeout(autoHideTimer);
            setAutoHideTimer(null);
        }
        if (currentIndex < images.length - 1) {
            setCurrentIndex((prev) => prev + 1);
            setShowResult(false);
            setUserChoice(null);
            setSelectedAnswer(null);
            setImageLoading(true); // Reset loading state for new image
            setHovering(false);
        }
        else {
            setGameComplete(true);
        }
    };
    const handleManualNext = () => {
        handleNext();
    };
    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            // Could add toast notification here
        });
    };
    const resetGame = () => {
        setCurrentIndex(0);
        setGameStats({
            correct: 0,
            incorrect: 0,
            streak: 0,
            bestStreak: 0,
        });
        setShowResult(false);
        setUserChoice(null);
        setSelectedAnswer(null);
        setGameComplete(false);
        fetchImages();
    };
    const shareResults = (platform: string) => {
        const score = Math.round((gameStats.correct / TOTAL_QUESTIONS) * 100);
        const text = `I scored ${score}% on the AI or Real Image Quiz! Can you tell the difference between AI-generated and real images? Test yourself at`;
        const url = window.location.href;
        const shareUrls: Record<string, string> = {
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        };
        if (platform === 'copy') {
            navigator.clipboard.writeText(`${text} ${url}`);
            // Could add a toast notification here
        }
        else if (shareUrls[platform]) {
            window.open(shareUrls[platform], '_blank');
        }
    };
    const getScoreEmoji = () => {
        const score = (gameStats.correct / TOTAL_QUESTIONS) * 100;
        if (score === 100)
            return '🏆';
        if (score >= 80)
            return '🌟';
        if (score >= 60)
            return '👍';
        if (score >= 40)
            return '🤔';
        return '📚';
    };
    const getScoreMessage = () => {
        const score = (gameStats.correct / TOTAL_QUESTIONS) * 100;
        if (score === 100)
            return "Perfect! You're an AI detection expert!";
        if (score >= 80)
            return 'Excellent! You have a keen eye!';
        if (score >= 60)
            return "Good job! You're getting the hang of it!";
        if (score >= 40)
            return 'Not bad! Keep practicing!';
        return 'AI is getting really good! Try again!';
    };
    if (loading) {
        return (<div className='min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
        <LandingPageNavigation />
        <div className='flex items-center justify-center min-h-[60vh]'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto'></div>
            <p className='mt-4 text-gray-600 dark:text-gray-300'>
              Loading challenge...
            </p>
          </div>
        </div>
      </div>);
    }
    return (<>
      <Seo templateTitle='AI or Real? - Test Your Detection Skills | Easy-Peasy.AI' description='Can you tell the difference between AI-generated and real images? Test your skills in this fun, interactive quiz!' image={`https://easy-peasy.ai/images/ai-or-real.jpeg`}/>

      <div className='min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
        <LandingPageNavigation />

        {/* Confetti Effect */}
        {showConfetti && (<Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={200} gravity={0.1}/>)}

        <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12'>
          {/* Header */}
          <div className='text-center mb-8'>
            <motion.h1 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className='text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4'>
              AI or Real?
            </motion.h1>
            <p className='text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto'>
              Can you tell the difference between AI-generated and real images?
              Test your detection skills in this interactive challenge!
            </p>
          </div>

          {!gameComplete ? (<>
              {/* Progress Bar */}
              <div className='mb-8'>
                <div className='flex justify-between items-center mb-2'>
                  <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Question {currentIndex + 1} of {TOTAL_QUESTIONS}
                  </span>
                  <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Score: {gameStats.correct}/
                    {currentIndex > 0 ? currentIndex : 0}
                  </span>
                </div>
                <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5'>
                  <motion.div className='bg-gradient-to-r from-purple-600 to-blue-600 h-2.5 rounded-full' initial={{ width: 0 }} animate={{
                width: `${((currentIndex + 1) / TOTAL_QUESTIONS) * 100}%`,
            }} transition={{ duration: 0.5 }}/>
                </div>
              </div>

              {/* Streak Indicator */}
              {gameStats.streak > 1 && (<motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className='text-center mb-4'>
                  <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'>
                    🔥 {gameStats.streak} in a row!
                  </span>
                </motion.div>)}

              {/* Image Display */}
              <div className='relative max-w-4xl mx-auto mb-8'>
                <AnimatePresence mode='wait'>
                  <motion.div key={currentIndex} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.3 }} className='relative'>
                    <div className='relative rounded-2xl overflow-hidden shadow-2xl'>
                      {/* Loading skeleton while image loads */}
                      {imageLoading && (<div className='w-full h-[600px] bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center'>
                          <div className='text-gray-400 dark:text-gray-500'>
                            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400 mx-auto'></div>
                            <p className='mt-4 text-sm'>Loading image...</p>
                          </div>
                        </div>)}

                      {images[currentIndex] && (<img src={images[currentIndex].url} alt='Quiz image' className={`w-full h-[600px] object-cover bg-gray-100 dark:bg-gray-800 transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`} style={{
                    objectPosition: 'center',
                }} onLoadStart={() => setImageLoading(true)} onLoad={() => setImageLoading(false)} onError={() => setImageLoading(false)}/>)}

                      {/* Result Overlay */}
                      <AnimatePresence>
                        {showResult && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={`absolute inset-0 flex items-center justify-center ${userChoice === images[currentIndex].isAI
                    ? 'bg-green-500/90'
                    : 'bg-red-500/90'}`} onMouseEnter={() => {
                    setHovering(true);
                    if (autoHideTimer) {
                        clearTimeout(autoHideTimer);
                        setAutoHideTimer(null);
                    }
                }} onMouseLeave={() => {
                    setHovering(false);
                    // Set a new timer to auto-advance if user leaves hover
                    const timer = setTimeout(() => {
                        handleNext();
                    }, 1500);
                    setAutoHideTimer(timer);
                }}>
                            <div className='text-white text-center p-8 max-w-2xl'>
                              <div className='text-6xl mb-4'>
                                {userChoice === images[currentIndex].isAI ? (<AiOutlineCheck className='mx-auto'/>) : (<AiOutlineClose className='mx-auto'/>)}
                              </div>
                              <h3 className='text-3xl font-bold mb-2'>
                                {userChoice === images[currentIndex].isAI
                    ? 'Correct!'
                    : 'Wrong!'}
                              </h3>
                              <p className='text-xl mb-4'>
                                This image is{' '}
                                {images[currentIndex].isAI
                    ? 'AI-generated'
                    : 'real'}
                              </p>

                              {/* AI Image Info */}
                              {images[currentIndex].isAI && (<div className='mt-4 space-y-3'>
                                  {images[currentIndex].description && (<div className='bg-white/10 backdrop-blur-sm rounded-lg p-3'>
                                      <p className='text-xs font-semibold mb-1'>
                                        Prompt:
                                      </p>
                                      <p className='text-sm opacity-90 cursor-pointer hover:bg-white/10 rounded p-1 transition-colors' onClick={() => copyToClipboard(images[currentIndex].description ||
                            '')} title='Click to copy prompt'>
                                        {images[currentIndex].description}
                                      </p>
                                      <p className='text-xs mt-1 opacity-70'>
                                        Click to copy
                                      </p>
                                    </div>)}
                                  {images[currentIndex].slug && (<a href={`https://easy-peasy.ai/ai-image-generator/images/${images[currentIndex].slug}`} target='_blank' rel='noopener noreferrer' className='inline-block bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium transition-colors'>
                                      View on Easy-Peasy.AI →
                                    </a>)}
                                </div>)}

                              {/* Real Image Info */}
                              {!images[currentIndex].isAI &&
                    images[currentIndex].photographer && (<div className='mt-4 space-y-2'>
                                    {/* <p className='text-sm'>
                      Photo by{' '}
                      <a
                        href={
                          images[currentIndex].photographer
                            ?.profileUrl
                        }
                        target='_blank'
                        rel='noopener noreferrer'
                        className='underline hover:no-underline font-medium'
                      >
                        {
                          images[currentIndex].photographer
                            ?.name
                        }
                      </a>
                    </p> */}
                                    <a href={images[currentIndex].originalUrl ||
                        'https://unsplash.com'} target='_blank' rel='noopener noreferrer' className='inline-block bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium transition-colors'>
                                      View on Unsplash →
                                    </a>
                                  </div>)}

                              {/* Manual Next Button */}
                              <button onClick={handleManualNext} className='mt-6 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg px-6 py-2 text-sm font-medium transition-colors'>
                                Next Question →
                              </button>
                            </div>
                          </motion.div>)}
                      </AnimatePresence>
                    </div>

                    {/* Attribution footer - shown after reveal for Unsplash images */}
                    {showResult &&
                !images[currentIndex].isAI &&
                images[currentIndex].photographer && (<motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className='mt-2 text-center text-xs text-gray-500 dark:text-gray-400'>
                          View original on{' '}
                          <a href={images[currentIndex].originalUrl ||
                    'https://unsplash.com'} target='_blank' rel='noopener noreferrer' className='underline hover:text-gray-700 dark:hover:text-gray-300'>
                            Unsplash
                          </a>
                        </motion.div>)}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Choice Buttons */}
              <div className='flex gap-4 justify-center max-w-2xl mx-auto'>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleChoice(true)} disabled={showResult} className={`flex-1 py-6 px-8 rounded-2xl font-bold text-lg transition-all ${selectedAnswer === 'ai' && showResult
                ? images[currentIndex].isAI
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'} ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'} shadow-lg`}>
                  <HiSparkles className='inline-block mr-2 text-2xl'/>
                  AI Generated
                </motion.button>

                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => handleChoice(false)} disabled={showResult} className={`flex-1 py-6 px-8 rounded-2xl font-bold text-lg transition-all ${selectedAnswer === 'real' && showResult
                ? !images[currentIndex].isAI
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'} ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'} shadow-lg`}>
                  <HiPhotograph className='inline-block mr-2 text-2xl'/>
                  Real Photo
                </motion.button>
              </div>
            </>) : (
        /* Results Screen */
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className='max-w-2xl mx-auto text-center'>
              <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12'>
                <div className='text-6xl mb-6'>{getScoreEmoji()}</div>

                <h2 className='text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent'>
                  Quiz Complete!
                </h2>

                <p className='text-xl text-gray-600 dark:text-gray-300 mb-8'>
                  {getScoreMessage()}
                </p>

                <div className='grid grid-cols-2 gap-4 mb-8'>
                  <div className='bg-green-50 dark:bg-green-900/20 rounded-xl p-4'>
                    <p className='text-3xl font-bold text-green-600 dark:text-green-400'>
                      {gameStats.correct}
                    </p>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Correct
                    </p>
                  </div>
                  <div className='bg-red-50 dark:bg-red-900/20 rounded-xl p-4'>
                    <p className='text-3xl font-bold text-red-600 dark:text-red-400'>
                      {gameStats.incorrect}
                    </p>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Incorrect
                    </p>
                  </div>
                </div>

                <div className='bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 mb-8'>
                  <p className='text-5xl font-bold text-purple-600 dark:text-purple-400'>
                    {Math.round((gameStats.correct / TOTAL_QUESTIONS) * 100)}%
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    Accuracy
                  </p>
                  {gameStats.bestStreak > 1 && (<p className='text-sm text-gray-600 dark:text-gray-400 mt-2'>
                      Best Streak: {gameStats.bestStreak} 🔥
                    </p>)}
                </div>

                {/* Share Buttons */}
                <div className='mb-8'>
                  <p className='text-sm text-gray-600 dark:text-gray-300 mb-4'>
                    Share your results and challenge your friends!
                  </p>
                  <div className='flex justify-center gap-3'>
                    <button onClick={() => shareResults('twitter')} className='p-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors' aria-label='Share on Twitter'>
                      <FaTwitter size={20}/>
                    </button>
                    <button onClick={() => shareResults('facebook')} className='p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors' aria-label='Share on Facebook'>
                      <FaFacebook size={20}/>
                    </button>
                    <button onClick={() => shareResults('linkedin')} className='p-3 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors' aria-label='Share on LinkedIn'>
                      <FaLinkedin size={20}/>
                    </button>
                    <button onClick={() => shareResults('copy')} className='p-3 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors' aria-label='Copy link'>
                      <FaLink size={20}/>
                    </button>
                  </div>
                </div>

                {/* Play Again Button */}
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={resetGame} className='w-full py-4 px-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg'>
                  <BiRefresh className='inline-block mr-2 text-2xl'/>
                  Play Again
                </motion.button>
              </div>
            </motion.div>)}
        </main>

        <Footer />
      </div>
    </>);
};
export default AIOrRealQuiz;
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
        revalidate: 3600, // Revalidate every hour
    };
};

    async function __Next_Translate__getStaticProps__1a1052e9196__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-or-real',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1052e9196__ as getStaticProps }
  